import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Star } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import ImageUploader from './ImageUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: '',
  bio: '',
  image_url: '',
  genres: '',
  city: '',
  instagram_url: '',
  spotify_url: '',
  is_featured: false,
};

const ArtistsPanel = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/artists`);
      setItems(res.data);
    } catch (e) {
      toast.error('Failed to load artists');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      bio: item.bio,
      image_url: item.image_url,
      genres: (item.genres || []).join(', '),
      city: item.city || '',
      instagram_url: item.instagram_url || '',
      spotify_url: item.spotify_url || '',
      is_featured: !!item.is_featured,
    });
    setOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        genres: form.genres.split(',').map((g) => g.trim()).filter(Boolean),
        city: form.city || null,
        instagram_url: form.instagram_url || null,
        spotify_url: form.spotify_url || null,
      };
      const url = editingId ? `${API}/artists/${editingId}` : `${API}/artists`;
      const method = editingId ? 'put' : 'post';
      await axios[method](url, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(editingId ? 'Artist updated' : 'Artist added');
      setOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this artist and all their votes?')) return;
    try {
      await axios.delete(`${API}/artists/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Artist removed');
      fetchItems();
    } catch {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <Loader2 size={32} className="animate-spin text-[#B5FF00] mx-auto" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-6">
        <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="create-artist-btn">
          <Plus size={18} />
          NEW ARTIST
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full" data-testid="artists-table">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">ARTIST</th>
              <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">CITY</th>
              <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">GENRES</th>
              <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">VOTES (WEEK / TOTAL)</th>
              <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">FEATURED</th>
              <th className="text-right py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-zinc-800/50 hover:bg-zinc-900/50" data-testid={`artist-row-${item.id}`}>
                <td className="py-4 px-4">
                  <div className="flex items-center gap-3">
                    {item.image_url && (
                      <img src={item.image_url} alt="" className="w-12 h-12 object-cover img-grayscale" />
                    )}
                    <span className="font-['Josefin_Sans'] text-white">{item.name}</span>
                  </div>
                </td>
                <td className="py-4 px-4 text-sm text-zinc-400">{item.city || '—'}</td>
                <td className="py-4 px-4">
                  <div className="flex flex-wrap gap-1">
                    {(item.genres || []).map((g) => (
                      <span key={g} className="font-mono text-[10px] text-[#B5FF00] bg-[#B5FF00]/10 px-2 py-0.5">
                        {g}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="py-4 px-4 font-mono text-sm text-zinc-300">
                  <span className="text-[#B5FF00]">{item.weekly_upvotes}</span> / {item.total_upvotes}
                </td>
                <td className="py-4 px-4">
                  {item.is_featured && (
                    <span className="inline-flex items-center gap-1 font-mono text-xs text-[#B5FF00] bg-[#B5FF00]/10 px-2 py-1">
                      <Star size={12} className="fill-[#B5FF00]" /> FEATURED
                    </span>
                  )}
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => openEdit(item)} className="p-2 text-zinc-400 hover:text-white" data-testid={`edit-artist-${item.id}`}>
                      <Pencil size={18} />
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="p-2 text-zinc-400 hover:text-red-500" data-testid={`delete-artist-${item.id}`}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && (
          <div className="text-center py-16 border border-zinc-800">
            <p className="text-zinc-500 font-mono">No artists yet. Add your first spotlight.</p>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Josefin_Sans'] text-2xl">
              {editingId ? 'EDIT ARTIST' : 'NEW ARTIST'}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 font-mono text-xs tracking-wider">
              Manage Discovery page artists — image, links, genres and featured status.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">NAME</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                  required
                  data-testid="artist-name-input"
                />
              </div>
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">CITY</label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                  data-testid="artist-city-input"
                />
              </div>
            </div>
            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">ARTIST IMAGE</label>
              <ImageUploader
                value={form.image_url}
                onChange={(url) => setForm({ ...form, image_url: url })}
                token={token}
                testIdPrefix="artist-image"
              />
            </div>
            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">GENRES (comma-separated)</label>
              <input
                type="text"
                value={form.genres}
                onChange={(e) => setForm({ ...form, genres: e.target.value })}
                className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                placeholder="Techno, House"
                data-testid="artist-genres-input"
              />
            </div>
            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">BIO</label>
              <textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                className="w-full h-32 bg-[#050505] border border-zinc-800 text-white px-4 py-3 focus:border-[#B5FF00] focus:outline-none resize-none"
                required
                data-testid="artist-bio-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">INSTAGRAM URL</label>
                <input
                  type="url"
                  value={form.instagram_url}
                  onChange={(e) => setForm({ ...form, instagram_url: e.target.value })}
                  className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                  placeholder="https://instagram.com/..."
                  data-testid="artist-instagram-input"
                />
              </div>
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">SPOTIFY URL</label>
                <input
                  type="url"
                  value={form.spotify_url}
                  onChange={(e) => setForm({ ...form, spotify_url: e.target.value })}
                  className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                  placeholder="https://open.spotify.com/artist/XXXXXXXXXXXXXXXXXXXXXX"
                  data-testid="artist-spotify-input"
                />
                <p className="font-mono text-[10px] text-zinc-500 mt-1">
                  Real artist profile URL only — search / playlist links are rejected. Leave blank if unsure.
                </p>
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                className="w-5 h-5"
                data-testid="artist-featured-input"
              />
              <span className="font-mono text-sm text-zinc-300">
                Set as <span className="text-[#B5FF00]">Featured Artist of the Week</span> (only one can be featured)
              </span>
            </label>
            <div className="flex gap-4 pt-4">
              <button type="button" onClick={() => setOpen(false)} className="btn-secondary flex-1">
                CANCEL
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="save-artist-btn"
              >
                {saving ? <><Loader2 size={18} className="animate-spin" />SAVING...</> : 'SAVE'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArtistsPanel;
